"use client";

import {
  IconDeviceDesktop,
  IconDotsVertical,
  IconLogout,
  IconMoon,
  IconNotification,
  IconSettings,
  IconSun,
  IconUserCircle,
  IconSwitchHorizontal,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useTheme } from "next-themes";
import { useRouter } from "next/navigation";
import { useMemo, useCallback } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { authApi } from "@/lib/client/auth-api";
import {
  getAuthorityBadgeVariant,
  getAuthorityDisplayName,
} from "@/lib/shared/constants";
import { useAuthStore } from "@/stores/auth-store";
import { isTenantAdmin } from "@/lib/shared/auth-utils";
import { useDelegation } from "@/hooks/use-delegation";

export function NavUser() {
  const { isMobile } = useSidebar();
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useTheme();
  const {
    isDelegating,
    availableTargets,
    isLoadingTargets,
    switchTo,
    returnToMyAccount,
    canDelegate,
    currentDelegation,
  } = useDelegation();

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onMutate: async () => {
      // Immediately redirect to login, don't wait for API
      router.push("/auth/login");
    },
    onSettled: async () => {
      // Always logout locally, even if API call fails
      await logout();
    },
  });

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const handleNavigation = (path: string) => {
    router.push(path);
  };

  const cycleTheme = useCallback(() => {
    const themes = ["light", "dark", "system"];
    const currentIndex = themes.indexOf(theme || "system");
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  }, [theme, setTheme]);

  const themeIcon = useMemo(() => {
    switch (theme) {
      case "light":
        return <IconSun className="h-4 w-4" />;
      case "dark":
        return <IconMoon className="h-4 w-4" />;
      case "system":
      default:
        return <IconDeviceDesktop className="h-4 w-4" />;
    }
  }, [theme]);

  const themeLabel = useMemo(() => {
    switch (theme) {
      case "light":
        return "Light Mode";
      case "dark":
        return "Dark Mode";
      case "system":
      default:
        return "System Mode";
    }
  }, [theme]);

  // Generate initials from name or email
  const initials = useMemo(() => {
    const fullName = user?.userInfo?.fullName;
    if (fullName) {
      const parts = fullName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0].slice(0, 1).toUpperCase()}${parts[parts.length - 1].slice(0, 1).toUpperCase()}`;
      }
      return fullName.slice(0, 2).toUpperCase();
    }
    if (user?.userInfo?.email) {
      return user.userInfo.email.slice(0, 2).toUpperCase();
    }
    return "TQ";
  }, [user?.userInfo?.fullName, user?.userInfo?.email]);

  const displayName = useMemo(() => {
    return user?.userInfo?.fullName || user?.userInfo?.email || "User";
  }, [user?.userInfo?.fullName, user?.userInfo?.email]);

  const authority = useMemo(() => user?.userInfo?.role || "", [user?.userInfo?.role]);
  const isUserTenantAdmin = useMemo(() => isTenantAdmin(user?.userInfo), [user?.userInfo]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{displayName}</span>
                <span className="text-muted-foreground truncate text-xs">
                  {getAuthorityDisplayName(authority)}
                </span>
              </div>
              <IconDotsVertical className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{displayName}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {getAuthorityDisplayName(authority)}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={() => handleNavigation("/settings")}>
                <IconUserCircle />
                Account Settings
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleNavigation("/notifications")}
              >
                <IconNotification />
                Notifications
              </DropdownMenuItem>
              {isUserTenantAdmin && (
                <DropdownMenuItem onClick={() => handleNavigation("/admin/system")}>
                  <IconSettings />
                  System Admin
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            
            {/* Delegation Section - Only show if user can delegate or is currently delegating */}
            {(canDelegate || isDelegating) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-xs text-muted-foreground px-2 py-1.5">
                    Switch to
                  </DropdownMenuLabel>
                  
                  {/* Return to My Account - only show when delegating */}
                  {isDelegating && (
                    <DropdownMenuItem onClick={returnToMyAccount}>
                      <IconSwitchHorizontal />
                      Return to My Account
                    </DropdownMenuItem>
                  )}
                  
                  {/* Show current delegation context */}
                  {isDelegating && currentDelegation && (
                    <DropdownMenuItem disabled className="opacity-60">
                      <span className="mr-2">
                        {currentDelegation.type === 'organization' ? '📊' : '🏢'}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-xs">Currently managing:</span>
                        <span className="font-medium">{currentDelegation.name}</span>
                      </div>
                    </DropdownMenuItem>
                  )}
                  
                  {/* Available delegation targets */}
                  {availableTargets.length > 0 && !isDelegating && (
                    <>
                      {availableTargets.map((target) => (
                        <DropdownMenuItem
                          key={`${target.type}-${target.id}`}
                          onClick={() => switchTo(target)}
                          disabled={isLoadingTargets}
                        >
                          <span className="mr-2">
                            {target.type === 'organization' ? '📊' : '🏢'}
                          </span>
                          <div className="flex flex-col">
                            <span>{target.name}</span>
                            {target.description && (
                              <span className="text-xs text-muted-foreground">
                                {target.description}
                              </span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                </DropdownMenuGroup>
              </>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={cycleTheme}>
              {themeIcon}
              {themeLabel}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
            >
              <IconLogout />
              {logoutMutation.isPending ? "Logging out..." : "Log out"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
